import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  ArrowRight, ArrowLeft, ChevronDown, ChevronUp,
  CircleCheck, AlertTriangle, CircleAlert, Clock, FileCheck, Shield,
  Compass, ShieldAlert, HelpCircle, Bookmark, Sparkles,
} from 'lucide-react';
import {
  trackVorentscheidungAngezeigt,
  trackStep3CtaClick,
} from '@/lib/tracking';
import { QualitaetsAnzeige } from './QualitaetsAnzeige';

const AMPEL_CONFIG = {
  gruen: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', icon: CircleCheck, iconColor: 'text-emerald-600' },
  gelb: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: AlertTriangle, iconColor: 'text-amber-500' },
  rot: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: CircleAlert, iconColor: 'text-red-500' },
};

const WEG_ICONS = {
  'Pragmatischer Schnellstart': Clock,
  'Strukturierter Standardweg': FileCheck,
  'Professionelle Vollplanung': Shield,
};

const KATEGORIE_ICONS = {
  betriebswirtschaftlich: { icon: Compass, cls: 'text-blue-600 bg-blue-50' },
  organisatorisch: { icon: FileCheck, cls: 'text-slate-600 bg-slate-50' },
  oekologisch: { icon: AlertTriangle, cls: 'text-emerald-600 bg-emerald-50' },
  gesetzlich: { icon: ShieldAlert, cls: 'text-red-600 bg-red-50' },
  foerderbezogen: { icon: Bookmark, cls: 'text-blue-600 bg-blue-50' },
};

export function StepEinschaetzung({ ansicht, onWeiter, onZurueck, onRegister, onProjektEntwurf, isPublic, t }) {
  const { einschaetzung, empfehlung, hinweise, vorentscheidung } = ansicht;
  const [showBegruendung, setShowBegruendung] = useState(false);
  const [showHinweise, setShowHinweise] = useState(false);

  const WegIcon = WEG_ICONS[empfehlung.empfohlener_weg] || FileCheck;
  const falltyp = ansicht?.erkannt?.erkannte_merkmale?.find(m => m.label === 'Massnahme' || m.label === 'Maßnahme')?.wert || 'unbekannt';
  const zielgruppe = ansicht?.zielgruppe?.key || 'waldbesitzer';

  useEffect(() => {
    if (vorentscheidung) {
      trackVorentscheidungAngezeigt(falltyp, zielgruppe);
    }
  }, [vorentscheidung, falltyp, zielgruppe]);

  return (
    <div data-testid="step-einschaetzung">
      {/* Qualitätsanzeige */}
      <QualitaetsAnzeige
        ansicht={ansicht}
        onZurueckZurEingabe={onZurueck}
        t={t}
      />

      <div className="mb-8">
        <h1 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mb-2">
          {t('fa.unsereEinschaetzung')}
        </h1>
        <p className="text-muted-foreground text-sm sm:text-base">
          {t('fa.soOrdnenWir')}
        </p>
      </div>

      {/* Ampel-Bewertung */}
      <div className="space-y-3 mb-8" data-testid="ampel-bereich">
        {einschaetzung.ampeln.map((a, i) => {
          const cfg = AMPEL_CONFIG[a.farbe] || AMPEL_CONFIG.gelb;
          const Icon = cfg.icon;
          return (
            <div key={i} className={`flex items-start gap-3 p-4 rounded-lg border ${cfg.bg} ${cfg.border}`} data-testid={`ampel-${a.farbe}-${i}`}>
              <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${cfg.iconColor}`} />
              <div>
                <p className={`text-sm font-semibold ${cfg.text}`}>{a.titel}</p>
                <p className={`text-sm mt-0.5 ${cfg.text} opacity-80`}>{a.erklaerung}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empfehlung */}
      <Card className="border-primary/20 shadow-soft mb-6" data-testid="empfehlung-card">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <WegIcon className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('fa.unsereEmpfehlung')}</p>
              <h3 className="font-serif text-lg font-bold text-foreground mb-2" data-testid="empfohlener-weg">
                {empfehlung.empfohlener_weg}
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-2" data-testid="empfehlung-beschreibung">
                {empfehlung.beschreibung}
              </p>
              {empfehlung.aufwand && (
                <p className="text-sm text-muted-foreground" data-testid="empfehlung-aufwand">
                  {t('fa.geschaetzterAufwand')} {empfehlung.aufwand}
                </p>
              )}
              {empfehlung.mehrfach_hinweis && (
                <div className="mt-3 p-3 rounded-md bg-amber-50 border border-amber-200" data-testid="mehrfach-hinweis">
                  <p className="text-sm text-amber-800">{empfehlung.mehrfach_hinweis}</p>
                </div>
              )}
              <button
                onClick={() => setShowBegruendung(!showBegruendung)}
                className="flex items-center gap-1 mt-3 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                data-testid="begruendung-toggle"
              >
                {showBegruendung ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                {t('fa.warumEmpfehlung')}
              </button>
              {showBegruendung && (
                <p className="mt-2 text-sm text-gray-600 bg-warm-50 rounded-md p-3" data-testid="begruendung-text">
                  {empfehlung.begruendung}
                </p>
              )}
              {empfehlung.perspektive_hinweis && (
                <div className="mt-4 p-3 rounded-md bg-primary/[0.04] border border-primary/10" data-testid="perspektive-hinweis">
                  <p className="text-sm text-foreground">{empfehlung.perspektive_hinweis}</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hinweise (aufklappbar) */}
      {hinweise.hinweise.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => setShowHinweise(!showHinweise)}
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-foreground transition-colors mb-3"
            data-testid="hinweise-toggle"
          >
            {showHinweise ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {hinweise.hinweise.length === 1 ? t('fa.hinweis1') : `${hinweise.hinweise.length} ${t('fa.hinweiseN')}`} {t('fa.anzeigen')}
          </button>
          {showHinweise && (
            <div className="space-y-2" data-testid="hinweise-list">
              {hinweise.hinweise.map((h, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-warm-50 border border-warm-200">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm text-foreground">{h.text}</p>
                    <p className="text-xs text-muted-foreground mt-1">{h.aktion}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* VORENTSCHEIDUNG */}
      {vorentscheidung && (
        <div className="mb-6" data-testid="vorentscheidung-section">
          <div className="border-t border-warm-200 pt-6 mt-2">
            <h2 className="font-serif text-lg sm:text-xl font-bold text-foreground mb-4">
              {t('fa.vorlaeufig')}
            </h2>

            <div className="flex items-start gap-3 p-4 rounded-lg border border-primary/15 bg-primary/[0.02] mb-3" data-testid="vorentscheidung-weg">
              <Compass className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-medium text-primary uppercase tracking-wide mb-0.5">{t('fa.sinnvollsterWeg')}</p>
                <p className="text-sm font-medium text-foreground leading-relaxed">{vorentscheidung.sinnvollster_weg}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-200 bg-amber-50/50 mb-3" data-testid="vorentscheidung-risiko">
              <ShieldAlert className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-medium text-amber-700 uppercase tracking-wide mb-0.5">{t('fa.groesstesRisiko')}</p>
                <p className="text-sm font-medium text-foreground leading-relaxed">{vorentscheidung.groesstes_risiko?.text}</p>
              </div>
            </div>

            {vorentscheidung.wichtigster_offener_punkt && (
              <div className="flex items-start gap-3 p-4 rounded-lg border border-warm-200 bg-warm-50/50 mb-4" data-testid="vorentscheidung-offener-punkt">
                <HelpCircle className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">{t('fa.nochZuKlaeren')}</p>
                  <p className="text-sm font-medium text-foreground">{vorentscheidung.wichtigster_offener_punkt.beschreibung}</p>
                  <p className="text-xs text-muted-foreground mt-1">{vorentscheidung.wichtigster_offener_punkt.loesungshinweis}</p>
                </div>
              </div>
            )}
          </div>

          {isPublic && (
            <div className="space-y-3 mt-4">
              <Button
                onClick={() => { trackStep3CtaClick('vertiefte_pruefung', falltyp); onWeiter(); }}
                className="w-full h-12 text-base font-medium"
                data-testid="cta-vertiefte-pruefung"
              >
                <Sparkles className="h-5 w-5 mr-2" />
                {t('produkt.ctaMehrKlarheit')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <p className="text-xs text-center text-muted-foreground leading-relaxed">
                Die erste Einordnung zeigt, wo etwas unklar oder kritisch sein kann. Die Fallklärung hilft, genau diese Punkte sauber zu klären – als PDF zum Mitnehmen.
              </p>

              <Button
                onClick={() => { trackStep3CtaClick('analyse_speichern', falltyp); (onProjektEntwurf || onRegister)?.(); }}
                variant="outline"
                className="w-full h-11 text-sm font-medium border-primary/20 hover:bg-primary/5"
                data-testid="cta-analyse-speichern"
              >
                <Bookmark className="h-4 w-4 mr-2" />
                {t('produkt.ctaAtoB')}
              </Button>
            </div>
          )}

          {!isPublic && (
            <Button onClick={onWeiter} className="w-full h-12 text-base font-medium mt-4" data-testid="weiter-btn-3">
              {t('produkt.ctaMehrKlarheit')}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      )}

      {!vorentscheidung && (
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button variant="outline" onClick={onZurueck} className="order-2 sm:order-1" data-testid="zurueck-btn-3">
            <ArrowLeft className="h-4 w-4 mr-2" /> {t('schnellcheck.zurueck')}
          </Button>
          <Button onClick={onWeiter} className="order-1 sm:order-2" data-testid="weiter-btn-3">
            {t('schnellcheck.weiter')}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      )}

      {vorentscheidung && (
        <div className="mt-3">
          <Button variant="outline" onClick={onZurueck} data-testid="zurueck-btn-3">
            <ArrowLeft className="h-4 w-4 mr-2" /> {t('schnellcheck.zurueck')}
          </Button>
        </div>
      )}
    </div>
  );
}
